import { useForm } from "@tanstack/react-form";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";
import {
  FieldErrorMessage,
  FormErrorAlert,
} from "@/components/stone/form-errors";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";
import { getErrorMessage, getFirstErrorMessage } from "@/lib/form-errors";
import { useUser } from "@/lib/user-context";
import Loader from "./loader";

export default function SignInForm({
  onSwitchToSignUp,
  showWorkerWebBlockedMessage = false,
}: {
  onSwitchToSignUp: () => void;
  showWorkerWebBlockedMessage?: boolean;
}) {
  const { isSessionLoading } = useUser();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    onSubmit: async ({ value }) => {
      setSubmitError(null);
      await authClient.signIn.email(
        {
          email: value.email,
          password: value.password,
        },
        {
          onSuccess: () => {
            setSubmitError(null);
            toast.success("Sign in successful");
          },
          onError: (error) => {
            const message = getErrorMessage(error, "Failed to sign in");
            setSubmitError(message);
            toast.error(message);
          },
        }
      );
    },
    validators: {
      onSubmit: z.object({
        email: z.email("Invalid email address"),
        password: z.string().min(8, "Password must be at least 8 characters"),
      }),
    },
  });

  if (isSessionLoading) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      {showWorkerWebBlockedMessage ? (
        <Alert variant="warning">
          <AlertTriangle />
          <AlertTitle>Please use the mobile app</AlertTitle>
          <AlertDescription>
            Download OS04 from the App Store or Google Play, then sign in there
            for worker tasks.
          </AlertDescription>
        </Alert>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
        className="space-y-4"
      >
        <div>
          <form.Field name="email">
            {(field) => {
              const fieldError = getFirstErrorMessage(
                field.state.meta.errors as ReadonlyArray<unknown>
              );

              return (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Email</Label>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="email"
                    value={field.state.value}
                    aria-invalid={Boolean(fieldError)}
                    onBlur={field.handleBlur}
                    onChange={(e) => {
                      if (submitError) {
                        setSubmitError(null);
                      }
                      field.handleChange(e.target.value);
                    }}
                  />
                  <FieldErrorMessage message={fieldError} />
                </div>
              );
            }}
          </form.Field>
        </div>

        <div>
          <form.Field name="password">
            {(field) => {
              const fieldError = getFirstErrorMessage(
                field.state.meta.errors as ReadonlyArray<unknown>
              );

              return (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Password</Label>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="password"
                    value={field.state.value}
                    aria-invalid={Boolean(fieldError)}
                    onBlur={field.handleBlur}
                    onChange={(e) => {
                      if (submitError) {
                        setSubmitError(null);
                      }
                      field.handleChange(e.target.value);
                    }}
                  />
                  <FieldErrorMessage message={fieldError} />
                </div>
              );
            }}
          </form.Field>
        </div>

        <FormErrorAlert message={submitError} />

        <form.Subscribe>
          {(state) => (
            <Button
              type="submit"
              className="w-full"
              disabled={!state.canSubmit || state.isSubmitting}
            >
              {state.isSubmitting ? "Signing In..." : "Sign In"}
            </Button>
          )}
        </form.Subscribe>
        <div className="mb-2 flex justify-center">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-foreground hover:underline underline-offset-4"
          >
            Forgot your password?
          </Link>
        </div>
      </form>

      <div className="text-center pt-4 border-t border-border">
        <Button
          variant="link"
          onClick={onSwitchToSignUp}
          className="font-medium text-foreground"
        >
          Need an account? Sign Up
        </Button>
      </div>
    </div>
  );
}
